import { useCallback, useEffect, useRef, useState } from 'react'

import { APP_COMMIT, VersionPayload } from '../config/version'

const DEFAULT_POLL_INTERVAL_MS = 5 * 60 * 1000
const VERSION_URL = '/version.json'

export interface UseVersionCheckOptions {
  pollIntervalMs?: number
  url?: string
}

export interface UseVersionCheckResult {
  isStale: boolean
  latest: VersionPayload | null
}

const fetchLatestVersion = async (url: string): Promise<VersionPayload | null> => {
  try {
    const response = await fetch(`${url}?ts=${Date.now()}`, { cache: 'no-store' })

    if (!response.ok) return null

    const data = (await response.json()) as Partial<VersionPayload>

    if (!data || typeof data.commit !== 'string') return null

    return {
      version: data.version ?? '',
      commit: data.commit,
      commitShort: data.commitShort ?? '',
      buildDate: data.buildDate ?? '',
      buildTime: data.buildTime ?? ''
    }
  } catch {
    return null
  }
}

const useVersionCheck = ({
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  url = VERSION_URL
}: UseVersionCheckOptions = {}): UseVersionCheckResult => {
  const [latest, setLatest] = useState<VersionPayload | null>(null)
  const mountedRef = useRef(true)

  const check = useCallback(async () => {
    const payload = await fetchLatestVersion(url)

    if (!mountedRef.current || !payload) return

    setLatest(payload)
  }, [url])

  useEffect(() => {
    mountedRef.current = true

    check()
    const intervalId = window.setInterval(check, pollIntervalMs)

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') check()
    }
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      mountedRef.current = false
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [check, pollIntervalMs])

  const isStale = Boolean(
    latest &&
      latest.commit &&
      APP_COMMIT &&
      latest.commit !== 'unknown' &&
      APP_COMMIT !== 'unknown' &&
      latest.commit !== APP_COMMIT
  )

  return { isStale, latest }
}

export default useVersionCheck
